// apps/frontend/src/lib/pipeline-validator.ts
import type { PipelineNode, PipelineEdge } from '@/pages/pipelines/types/pipeline';
import type {
  ValidationResult,
  ValidationIssue,
  NodeValidation,
  EdgeValidation,
} from './validation-types';
import {
  calculateHealthScore,
  detectCycles,
  findOrphanNodes,
  topologicalSort,
} from './validation-utils';
import { validateConnection } from './connection-manager';

/**
 * PipelineValidator - Comprehensive pipeline validation class
 *
 * Validates entire pipelines by checking:
 * - Topology (cycles, orphans, execution order)
 * - Nodes (configuration, connections, type-specific rules)
 * - Edges (valid connections)
 * - Pipeline structure (SOURCE→DESTINATION path, required nodes)
 */
export class PipelineValidator {
  /**
   * Main validation method - validates entire pipeline
   *
   * @param nodes Array of pipeline nodes
   * @param edges Array of pipeline edges
   * @returns Complete validation result with issues and health score
   */
  validate(nodes: PipelineNode[], edges: PipelineEdge[]): ValidationResult {
    const result: ValidationResult = {
      isValid: true,
      healthScore: 100,
      errors: [],
      warnings: [],
      suggestions: [],
      nodeValidation: new Map<string, NodeValidation>(),
      edgeValidation: new Map<string, EdgeValidation>(),
      executionOrder: null,
    };

    const safeNodes = this.sanitizeNodes(Array.isArray(nodes) ? nodes : [], result);
    const safeEdges = this.sanitizeEdges(Array.isArray(edges) ? edges : [], result);

    // Rule 1: Empty pipeline check
    if (safeNodes.length === 0) {
      result.errors.push({
        type: 'pipeline',
        severity: 'error',
        message:
          nodes.length === 0
            ? 'Pipeline is empty. Add at least one SOURCE and one DESTINATION node.'
            : 'Pipeline has no valid nodes. Fix malformed node payload before running validation.',
      });
      result.isValid = false;
      result.healthScore = 0;
      return result;
    }

    // Initialize node validations
    safeNodes.forEach(node => {
      result.nodeValidation.set(node.id, {
        nodeId: node.id,
        isValid: true,
        isConfigured: !!node.data.config,
        errors: [],
        warnings: [],
        suggestions: [],
      });
    });

    // Run validation steps
    this.validateTopology(safeNodes, safeEdges, result);
    this.validateNodes(safeNodes, safeEdges, result);
    this.validateEdges(safeNodes, safeEdges, result);
    this.validatePipelineStructure(safeNodes, safeEdges, result);

    // Collect all issues for health score calculation
    const totalErrors = result.errors.length;
    const totalWarnings = result.warnings.length;

    // Rule 13: Large pipeline suggestion (>20 nodes)
    if (safeNodes.length > 20) {
      result.suggestions.push({
        type: 'pipeline',
        severity: 'info',
        message: `Large pipeline detected (${safeNodes.length} nodes). Consider splitting into smaller pipelines for better maintainability.`,
      });
    }

    // Calculate final health score
    result.healthScore = calculateHealthScore(totalErrors, totalWarnings, safeNodes.length);
    result.isValid = totalErrors === 0;

    return result;
  }

  /**
   * Sanitize nodes from runtime payload to avoid UI crashes from malformed AI output.
   */
  private sanitizeNodes(nodes: PipelineNode[], result: ValidationResult): PipelineNode[] {
    const safeNodes: PipelineNode[] = [];
    const seenNodeIds = new Set<string>();

    nodes.forEach((rawNode, index) => {
      if (!rawNode || typeof rawNode !== 'object') {
        result.warnings.push({
          type: 'pipeline',
          severity: 'warning',
          message: `Malformed node at index ${index} skipped during validation.`,
        });
        return;
      }

      const nodeRecord = rawNode as unknown as Record<string, unknown>;
      const nodeId =
        typeof nodeRecord.id === 'string' ? nodeRecord.id.trim() : '';

      if (!nodeId) {
        result.warnings.push({
          type: 'pipeline',
          severity: 'warning',
          message: `Node without valid id at index ${index} skipped during validation.`,
        });
        return;
      }

      if (seenNodeIds.has(nodeId)) {
        result.warnings.push({
          type: 'pipeline',
          severity: 'warning',
          message: `Duplicate node id "${nodeId}" detected. Later entry was skipped.`,
          nodeId,
        });
        return;
      }
      seenNodeIds.add(nodeId);

      const rawData =
        nodeRecord.data && typeof nodeRecord.data === 'object'
          ? (nodeRecord.data as Record<string, unknown>)
          : {};
      const rawPosition =
        nodeRecord.position && typeof nodeRecord.position === 'object'
          ? (nodeRecord.position as Record<string, unknown>)
          : {};

      const normalizedNode: PipelineNode = {
        id: nodeId,
        type:
          typeof nodeRecord.type === 'string' && nodeRecord.type.trim().length > 0
            ? nodeRecord.type
            : 'custom',
        position: {
          x: typeof rawPosition.x === 'number' && Number.isFinite(rawPosition.x) ? rawPosition.x : 0,
          y: typeof rawPosition.y === 'number' && Number.isFinite(rawPosition.y) ? rawPosition.y : 0,
        },
        data: {
          ...(rawData as unknown as PipelineNode['data']),
          label:
            typeof rawData.label === 'string' && rawData.label.trim().length > 0
              ? rawData.label
              : nodeId,
          type: (
            typeof rawData.type === 'string' && rawData.type.trim().length > 0
              ? rawData.type
              : typeof nodeRecord.type === 'string' && nodeRecord.type.trim().length > 0
                ? nodeRecord.type
                : 'TRANSFORMATION'
          ) as PipelineNode['data']['type'],
        },
      };

      safeNodes.push(normalizedNode);
    });

    return safeNodes;
  }

  /**
   * Sanitize edges from runtime payload to avoid crashes caused by malformed references.
   */
  private sanitizeEdges(edges: PipelineEdge[], result: ValidationResult): PipelineEdge[] {
    const safeEdges: PipelineEdge[] = [];
    const seenEdgeIds = new Set<string>();

    edges.forEach((rawEdge, index) => {
      if (!rawEdge || typeof rawEdge !== 'object') {
        result.warnings.push({
          type: 'pipeline',
          severity: 'warning',
          message: `Malformed edge at index ${index} skipped during validation.`,
        });
        return;
      }

      const edgeRecord = rawEdge as unknown as Record<string, unknown>;
      const source =
        typeof edgeRecord.source === 'string' ? edgeRecord.source.trim() : '';
      const target =
        typeof edgeRecord.target === 'string' ? edgeRecord.target.trim() : '';

      if (!source || !target) {
        result.warnings.push({
          type: 'pipeline',
          severity: 'warning',
          message: `Edge at index ${index} missing source/target and was skipped.`,
        });
        return;
      }

      const rawId =
        typeof edgeRecord.id === 'string' ? edgeRecord.id.trim() : '';
      let edgeId = rawId || `edge-${source}-${target}-${index + 1}`;

      if (seenEdgeIds.has(edgeId)) {
        edgeId = `${edgeId}-${index + 1}`;
        result.warnings.push({
          type: 'edge',
          severity: 'warning',
          message: `Duplicate edge id detected, reassigned to "${edgeId}" during validation.`,
          edgeId,
        });
      }
      seenEdgeIds.add(edgeId);

      safeEdges.push({
        id: edgeId,
        source,
        target,
        label: typeof edgeRecord.label === 'string' ? edgeRecord.label : undefined,
      });
    });

    return safeEdges;
  }

  /**
   * Validate topology: cycles, orphans, execution order
   */
  private validateTopology(
    nodes: PipelineNode[],
    edges: PipelineEdge[],
    result: ValidationResult
  ): void {
    // Rule 2: Detect orphan nodes
    const orphanNodes = findOrphanNodes(nodes, edges);
    orphanNodes.forEach(nodeId => {
      const issue: ValidationIssue = {
        type: 'node',
        severity: 'warning',
        message: 'Orphan node: This disconnected node will not be executed.',
        nodeId,
      };
      result.warnings.push(issue);

      const nodeValidation = result.nodeValidation.get(nodeId);
      if (nodeValidation) {
        nodeValidation.warnings.push(issue);
      }
    });

    // Rule 3: Detect cycles
    const cycles = detectCycles(nodes, edges);
    if (cycles.length > 0) {
      cycles.forEach(cycle => {
        const cycleStr = cycle.join(' → ');
        result.errors.push({
          type: 'pipeline',
          severity: 'error',
          message: `Circular dependency cycle detected: ${cycleStr}. Remove cycles to enable execution.`,
        });
      });
      result.executionOrder = null;
    } else {
      // Calculate execution order using topological sort
      result.executionOrder = topologicalSort(nodes, edges);
    }
  }

  /**
   * Validate individual nodes: configuration and type-specific rules
   */
  private validateNodes(
    nodes: PipelineNode[],
    edges: PipelineEdge[],
    result: ValidationResult
  ): void {
    // Build adjacency maps for input/output counting
    const inputCount = new Map<string, number>();
    const outputCount = new Map<string, number>();

    nodes.forEach(node => {
      inputCount.set(node.id, 0);
      outputCount.set(node.id, 0);
    });

    edges.forEach(edge => {
      outputCount.set(edge.source, (outputCount.get(edge.source) || 0) + 1);
      inputCount.set(edge.target, (inputCount.get(edge.target) || 0) + 1);
    });

    nodes.forEach(node => {
      const nodeValidation = result.nodeValidation.get(node.id);
      if (!nodeValidation) return;

      const inputs = inputCount.get(node.id) || 0;
      const outputs = outputCount.get(node.id) || 0;

      // Rule 4: Missing node configuration
      if (!node.data.config) {
        const issue: ValidationIssue = {
          type: 'node',
          severity: 'error',
          message: 'Missing configuration. Please configure this node before running the pipeline.',
          nodeId: node.id,
        };
        result.errors.push(issue);
        nodeValidation.errors.push(issue);
        nodeValidation.isValid = false;
        nodeValidation.isConfigured = false;
      }

      // Type-specific validation
      switch (node.data.type) {
        case 'SOURCE':
          // Rule 5: SOURCE with no outputs
          if (outputs === 0) {
            const issue: ValidationIssue = {
              type: 'node',
              severity: 'warning',
              message: 'SOURCE node has no outputs. Connect it to downstream nodes.',
              nodeId: node.id,
            };
            result.warnings.push(issue);
            nodeValidation.warnings.push(issue);
          }
          break;

        case 'DESTINATION':
          // Rule 6: DESTINATION with outputs
          if (outputs > 0) {
            const issue: ValidationIssue = {
              type: 'node',
              severity: 'error',
              message: 'DESTINATION nodes cannot have outputs. Remove outgoing connections.',
              nodeId: node.id,
            };
            result.errors.push(issue);
            nodeValidation.errors.push(issue);
            nodeValidation.isValid = false;
          }

          // Rule 7: DESTINATION with no inputs
          if (inputs === 0) {
            const issue: ValidationIssue = {
              type: 'node',
              severity: 'warning',
              message: 'DESTINATION node has no inputs. Connect it to upstream nodes.',
              nodeId: node.id,
            };
            result.warnings.push(issue);
            nodeValidation.warnings.push(issue);
          }
          break;

        case 'JOIN':
          // Rule 8: JOIN with less than 2 inputs
          if (inputs < 2) {
            const issue: ValidationIssue = {
              type: 'node',
              severity: 'error',
              message: 'JOIN nodes require at least 2 input connections to merge data streams.',
              nodeId: node.id,
            };
            result.errors.push(issue);
            nodeValidation.errors.push(issue);
            nodeValidation.isValid = false;
          }
          break;

        case 'TRANSFORMATION':
        case 'FILTER':
        case 'AGGREGATION':
          // Rule 9: Transformation with no input
          if (inputs === 0) {
            const issue: ValidationIssue = {
              type: 'node',
              severity: 'warning',
              message: `${node.data.type} node has no input. Connect it to upstream nodes.`,
              nodeId: node.id,
            };
            result.warnings.push(issue);
            nodeValidation.warnings.push(issue);
          }
          break;
      }
    });
  }

  /**
   * Validate edges: check connection validity
   */
  private validateEdges(
    nodes: PipelineNode[],
    edges: PipelineEdge[],
    result: ValidationResult
  ): void {
    const nodeMap = new Map<string, PipelineNode>();
    nodes.forEach(node => nodeMap.set(node.id, node));

    edges.forEach(edge => {
      const sourceNode = nodeMap.get(edge.source);
      const targetNode = nodeMap.get(edge.target);

      if (!sourceNode || !targetNode) {
        result.edgeValidation.set(edge.id, {
          edgeId: edge.id,
          isValid: false,
          severity: 'error',
          message: 'Edge connects to non-existent node',
        });
        return;
      }

      const validation = validateConnection(sourceNode, targetNode, edges);

      result.edgeValidation.set(edge.id, {
        edgeId: edge.id,
        isValid: validation.valid,
        severity: validation.severity,
        message: validation.message,
      });

      if (!validation.valid && validation.severity === 'error') {
        result.errors.push({
          type: 'edge',
          severity: 'error',
          message: validation.message,
          edgeId: edge.id,
        });
      } else if (!validation.valid && validation.severity === 'warning') {
        result.warnings.push({
          type: 'edge',
          severity: 'warning',
          message: validation.message,
          edgeId: edge.id,
        });
      }
    });
  }

  /**
   * Validate overall pipeline structure: SOURCE, DESTINATION, complete path
   */
  private validatePipelineStructure(
    nodes: PipelineNode[],
    edges: PipelineEdge[],
    result: ValidationResult
  ): void {
    const sourceNodes = nodes.filter(n => n.data.type === 'SOURCE');
    const destNodes = nodes.filter(n => n.data.type === 'DESTINATION');

    // Rule 10: No SOURCE node
    if (sourceNodes.length === 0) {
      result.errors.push({
        type: 'pipeline',
        severity: 'error',
        message: 'Pipeline must have at least one SOURCE node to fetch data.',
      });
    }

    // Rule 11: No DESTINATION node
    if (destNodes.length === 0) {
      result.errors.push({
        type: 'pipeline',
        severity: 'error',
        message: 'Pipeline must have at least one DESTINATION node to write data.',
      });
    }

    // Rule 12: No complete SOURCE→DESTINATION path
    if (sourceNodes.length > 0 && destNodes.length > 0) {
      const hasPath = this.hasPathFromSourceToDestination(nodes, edges);
      if (!hasPath) {
        result.errors.push({
          type: 'pipeline',
          severity: 'error',
          message: 'No complete path from SOURCE to DESTINATION. Connect your nodes to form a valid data flow.',
        });
      }
    }
  }

  /**
   * Check if there's at least one complete path from any SOURCE to any DESTINATION
   * Uses BFS (Breadth-First Search) to find paths
   */
  private hasPathFromSourceToDestination(
    nodes: PipelineNode[],
    edges: PipelineEdge[]
  ): boolean {
    const sourceNodes = nodes.filter(n => n.data.type === 'SOURCE');
    const destNodes = nodes.filter(n => n.data.type === 'DESTINATION');

    if (sourceNodes.length === 0 || destNodes.length === 0) {
      return false;
    }

    // Build adjacency list
    const adjacencyList = new Map<string, string[]>();
    nodes.forEach(node => adjacencyList.set(node.id, []));
    edges.forEach(edge => {
      const neighbors = adjacencyList.get(edge.source) || [];
      neighbors.push(edge.target);
      adjacencyList.set(edge.source, neighbors);
    });

    const destNodeIds = new Set(destNodes.map(n => n.id));

    // BFS from each source to find if any can reach any destination
    for (const sourceNode of sourceNodes) {
      const queue: string[] = [sourceNode.id];
      const visited = new Set<string>();
      visited.add(sourceNode.id);

      while (queue.length > 0) {
        const current = queue.shift();
        if (!current) break;

        // Check if we reached a destination
        if (destNodeIds.has(current)) {
          return true;
        }

        const neighbors = adjacencyList.get(current) || [];
        for (const neighbor of neighbors) {
          if (!visited.has(neighbor)) {
            visited.add(neighbor);
            queue.push(neighbor);
          }
        }
      }
    }

    return false;
  }
}
